import { SHOP_SOURCES } from "./constants/shopSources";
import { LINK_TARGETS } from "./constants/linkTargets";
import { getRefIdentifier, parseRef } from "./refs";
import { getProduct } from "../services/products.service";
import { getCategory } from "../services/categories.service";
import { fetchPageContent } from "../magnolia.service";

// Reduces a commerce link target's value to what is needed to look the target up again: the
// commerce source and one identifier. The value is only used as an identifier — the target
// data itself is always fetched (see hooks/useLinkTarget.js).
const toCommerceRef = (value) => {
    if (!value) return null;

    // Unresolved value: either the picker's JSON ref or a bare path/uuid.
    if (typeof value === "string") {
        const parsed = parseRef(value);
        return parsed ? toCommerceRef(parsed) : { source: SHOP_SOURCES.JCR, id: value.trim() };
    }

    const id = getRefIdentifier(value);

    return id ? { source: value.source || SHOP_SOURCES.JCR, id } : null;
};

// Everything the Card Teaser needs to know about a link target type, in one place:
//  - getRef:    the target value -> {source, id}, or null when it cannot be looked up
//  - fetch:     {source, id}, lang -> the target's data
//  - normalize: the fetched data -> the fields the card can fall back to ({title, text, image})
//  - inline:    the pages endpoint already resolves the target in place, so there is nothing to
//               fetch: the target value itself is normalized (no getRef/fetch, see getInlineTarget)
const LINK_TARGET_TYPES = {
    [LINK_TARGETS.ECOMMERCE_PRODUCTS]: {
        getRef: toCommerceRef,
        fetch: ({ source, id }, lang) => getProduct({ lang, source, productId: id }),
        normalize: (target) => ({
            title: target.displayName || target["@name"],
            text: target.shortDescription,
            image: Array.isArray(target.image) ? target.image[0] : target.image,
        }),
    },
    [LINK_TARGETS.ECOMMERCE_CATEGORIES]: {
        getRef: toCommerceRef,
        fetch: ({ source, id }, lang) => getCategory({ lang, source, categoryId: id }),
        normalize: (target) => ({
            title: target.label || target.displayName || target.name || target["@name"],
            text: target.heroText,
            image: target.heroImage,
        }),
    },
    [LINK_TARGETS.WEBSITE]: {
        // Pages are read by node path; a bare uuid cannot be looked up.
        getRef: (value) => {
            const path = typeof value === "object" ? value?.["@path"] : null;

            return path ? { source: SHOP_SOURCES.JCR, id: path } : null;
        },
        fetch: ({ id }, lang) => fetchPageContent(id, lang),
        normalize: (target) => ({
            title: target.title || target["@name"],
            text: target.meta?.description,
            image: target.meta?.ogImage,
        }),
    },
    [LINK_TARGETS.BLOG]: {
        inline: true,
        normalize: (target) => ({
            title: target.title || target["@name"],
            text: target.text,
            image: target.image?.image,
        }),
    },
    [LINK_TARGETS.EVENTS]: {
        inline: true,
        normalize: (target) => ({
            title: target.title || target["@name"],
            text: target.text,
            image: target.image?.image,
        }),
    },
    [LINK_TARGETS.B2B_PROFILES]: {
        inline: true,
        normalize: (target) => ({
            title: target.name || [target.firstname, target.lastname].filter(Boolean).join(" ") || target["@name"],
            text: target.title || target.company,
            image: target.image?.image,
        }),
    },
};

// `field` comes from content; "none", "external" and the SPA-internal "artificial" have no entry.
const getType = (field) => (Object.hasOwn(LINK_TARGET_TYPES, field) ? LINK_TARGET_TYPES[field] : null);

// Returns {field, source, id} for the link targets that can be fetched, otherwise null
// (no link, "none", "external", an inline type, or a value we cannot look up).
export const getLinkTargetRef = (link) => {
    const { field } = link?.target || {};
    const ref = getType(field)?.getRef?.(link.target[field]);

    return ref ? { field, ...ref } : null;
};

// The normalized target of an `inline` type, read straight from the resolved link value;
// null for every other type and for an unresolved value (a bare uuid).
export const getInlineTarget = (link) => {
    const { field } = link?.target || {};
    const value = link?.target?.[field];

    return getType(field)?.inline && value && typeof value === "object" ? normalizeTarget(field, value) : null;
};

export const fetchLinkTarget = ({ field, source, id }, lang) => getType(field).fetch({ source, id }, lang);

// Maps a fetched target to the fields CardTeaser can fall back to, so the component does not
// need to know about product, category or page shapes.
export const normalizeTarget = (field, target) => (target && getType(field)?.normalize(target)) || {};

// Translation key of the default CTA label of a target type, e.g. tile.ecommerce-products.cta
// (same naming as demo-ftl-base's tile.<type>.cta). Null for types without a fetchable target.
export const getDefaultCtaKey = (field) => (getType(field) ? `tile.${field}.cta` : null);
